/* eslint-disable jsx-a11y/anchor-is-valid */
/**
 * NavBar layout split (Tailwind `sm` ≈ 640px):
 * - Desktop-only: horizontal nav in `hidden sm:block` — uses `renderNavItem` (Headless UI Menu for Events).
 * - Mobile-only: hamburger + `Disclosure.Panel` with `sm:hidden` — uses `renderMobileNavItem`.
 * Shared: logo row, `activeEvents` state, route helpers, avatar helpers.
 */
import { Fragment, useEffect, useState, useContext } from "react";
import { Disclosure, Menu, Transition } from "@headlessui/react";
import { Link, useLocation } from "react-router-dom";
import { IoMenu, IoClose, IoChevronDown } from "react-icons/io5";
import routes from "../routes/menuRoutes";
import CompanyLogo from "./CompanyLogo";
import GlobalContext from "../contexts/store";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

/** Shared: used by both desktop Events menu and mobile Events list. */
function getEventAvatarSrc(event) {
  return (
    event?.organization?.logoDark ||
    event?.organization?.logo ||
    event?.promo?.post ||
    event?.promo?.story ||
    event?.promo?.banner ||
    ""
  );
}

/** Shared: fallback when no image URL is available. */
function getEventInitial(event) {
  const name = event?.organization?.name || event?.name || "";
  return name.trim().charAt(0).toUpperCase() || "?";
}

export default function NavBar() {
  const { upcomingEvents } = useContext(GlobalContext);
  const location = useLocation();
  const [activeEvents, setActiveEvents] = useState([]);
  /** Mobile-only: collapsible Events section in the hamburger panel. */
  const [showMobileEvents, setShowMobileEvents] = useState(false);

  useEffect(() => {
    setActiveEvents(upcomingEvents || []);
  }, [upcomingEvents]);

  const isActive = (path) => location.pathname === path;

  /** Shared: filters routes for both desktop and mobile nav lists. */
  const getVisibleRoutes = (routes) => {
    return routes.filter((route) => {
      if (route?.dropdown) return activeEvents.length > 0;
      if (route?.path === "/galleries") return false;
      return true;
    });
  };

  /**
   * Desktop-only (`hidden sm:block` parent).
   * Renders top-row links; Events uses a wide viewport-centered mega-menu (`Menu` + fixed `Menu.Items`).
   */
  const renderNavItem = (item) => {
    // Events — desktop mega-menu (not used on mobile; mobile uses `renderMobileNavItem`)
    if (item.dropdown && activeEvents.length > 0) {
      const isEventsActive = location.pathname.includes("/events/");
      
      return (
        <Menu as="div" className="relative inline-block text-left">
          <div>
            <Menu.Button className={classNames(
              "relative px-3 py-2 text-sm font-medium no-underline rounded-none flex items-center gap-1",
              isEventsActive
                ? "text-white after:w-full"
                : "text-gray-300 hover:text-gray-400 hover:after:w-full after:w-0",
              "after:absolute after:bottom-0 after:left-0 after:h-[1px] after:bg-gray-300",
              "after:transition-all after:duration-300 after:ease-in-out"
            )}>
              {item.name}
              <IoChevronDown size={16} />
            </Menu.Button>
          </div>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition ease-in duration-75"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            {/* Desktop-only: fixed panel so the wide menu stays viewport-centered (not scroll-trapped in the nav). */}
            <Menu.Items className="fixed left-1/2 z-[100] top-24 w-[min(1600px,calc(100vw-1.25rem))] -translate-x-1/2 origin-top rounded-2xl bg-[#0a0a0a] shadow-2xl ring-[0.5px] ring-white/10 focus:outline-none overflow-visible">
              {/* <div className="px-8 pt-7 pb-5 border-b border-white/10">
                <p className="text-lg font-semibold text-white tracking-tight">
                  Upcoming events
                </p>
                <p className="text-sm text-gray-400 mt-1.5">
                  {activeEvents.length} {activeEvents.length === 1 ? "show" : "shows"}: pick one to view tickets and details
                </p>
              </div> */}
              <div className="px-6 py-6 sm:px-8 sm:py-7" role="none">
                <p className="text-sm text-gray-400">
                  {activeEvents.length} {activeEvents.length === 1 ? "show" : "shows"}: pick one to view tickets and details
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-3 gap-y-1">
                {activeEvents.map((event, index) => {
                  const eventSlug = event?.slug || event?.organization?.slug;
                  const eventPath = `/events/event/${eventSlug}`;
                  const isCurrentEvent = location.pathname === eventPath;
                  const avatarSrc = getEventAvatarSrc(event);
                  
                  return (
                    <Menu.Item key={event.id || event._id || `event-${index}-${event?.organization?._id || event?.organization?.name}`}>
                      {({ active }) => (
                        <Link
                          to={eventPath}
                          className={classNames(
                            active || isCurrentEvent
                              ? "bg-white/10 text-white"
                              : "text-gray-200 hover:bg-white/5 hover:text-white",
                            "group flex items-center min-w-0 rounded-xl px-3 py-2.5 text-sm font-medium no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a]"
                          )}
                          title={event.organization.name}
                        >
                          {avatarSrc ? (
                            <img
                              src={avatarSrc}
                              alt=""
                              aria-hidden="true"
                              loading="lazy"
                              decoding="async"
                              className="h-9 w-9 rounded-full object-cover flex-shrink-0 mr-2.5 ring-2 ring-white/10"
                            />
                          ) : (
                            <span
                              aria-hidden="true"
                              className="h-9 w-9 rounded-full bg-gray-600 text-white text-sm font-semibold flex items-center justify-center flex-shrink-0 mr-2.5 ring-2 ring-white/10"
                            >
                              {getEventInitial(event)}
                            </span>
                          )}
                          <span className="min-w-0 leading-snug truncate">{event.organization.name}</span>
                        </Link>
                      )}
                    </Menu.Item>
                  );
                })}
                </div>
              </div>
            </Menu.Items>
          </Transition>
        </Menu>
      );
    }

    // Desktop-only: standard top-nav link (non-Events routes)
    return (
      <Link
        to={item.path}
        className={classNames(
          "relative px-3 py-2 text-sm font-medium no-underline rounded-none",
          isActive(item.path)
            ? "text-white after:w-full"
            : "text-gray-300 hover:text-gray-400 hover:after:w-full after:w-0",
          "after:absolute after:bottom-0 after:left-0 after:h-[1px] after:bg-gray-300",
          "after:transition-all after:duration-300 after:ease-in-out"
        )}
        aria-current={isActive(item.path) ? "page" : undefined}
      >
        {item.name}
      </Link>
    );
  };

  /**
   * Mobile-only (`Disclosure.Panel` + `sm:hidden`).
   * Full-width stacked links; Events is a toggle + scrollable list (different UX from desktop mega-menu).
   */
  const renderMobileNavItem = (item) => {
    // Mobile-only: Events — collapsible section + scrollable list
    if (item.dropdown && activeEvents.length > 0) {
      return (
        <Fragment>
          <button
            type="button"
            onClick={() => setShowMobileEvents((prev) => !prev)}
            className="w-full flex items-center justify-between px-3 py-2 text-base font-medium text-gray-300 border-b border-gray-700"
            aria-expanded={showMobileEvents}
            aria-controls="mobile-events-list"
          >
            <span>{item.name}</span>
            <span className="text-xs text-gray-400">({activeEvents.length})</span>
          </button>
          {showMobileEvents && (
            <div id="mobile-events-list" className="max-h-64 overflow-y-auto py-1 bg-white/5">
              {activeEvents.map((event, index) => {
                const eventSlug = event?.slug || event?.organization?.slug;
                const eventPath = `/events/event/${eventSlug}`;
                const isCurrentEvent = location.pathname === eventPath;
                
                return (
                  <Disclosure.Button
                    key={event.id || event._id || `mobile-event-${index}-${event?.organization?._id || event?.organization?.name}`}
                    as={Link}
                    to={eventPath}
                    className={classNames(
                      isCurrentEvent
                        ? "bg-gray-900 text-white"
                        : "text-gray-300 hover:bg-gray-700 hover:text-white",
                      "block rounded-md px-6 py-2 text-sm font-medium no-underline ml-3 mr-1"
                    )}
                    aria-current={isCurrentEvent ? "page" : undefined}
                  >
                    <span className="flex items-center">
                      {getEventAvatarSrc(event) ? (
                        <img
                          src={getEventAvatarSrc(event)}
                          alt=""
                          aria-hidden="true"
                          loading="lazy"
                          decoding="async"
                          className="h-7 w-7 rounded-full object-cover flex-shrink-0 mr-2"
                        />
                      ) : (
                        <span
                          aria-hidden="true"
                          className="h-7 w-7 rounded-full bg-gray-600 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0 mr-2"
                        >
                          {getEventInitial(event)}
                        </span>
                      )}
                      <span className="block truncate">{event.organization.name}</span>
                    </span>
                  </Disclosure.Button>
                );
              })}
            </div>
          )}
        </Fragment>
      );
    }

    // Mobile-only: standard nav row (non-Events routes)
    return (
      <Disclosure.Button
        as={Link}
        to={item.path}
        className={classNames(
          isActive(item.path)
            ? "bg-gray-900 text-white"
            : "text-gray-300 hover:bg-gray-700 hover:text-white",
          "block rounded-md px-3 py-2 text-base font-medium no-underline"
        )}
        aria-current={isActive(item.path) ? "page" : undefined}
      >
        {item.name}
      </Disclosure.Button>
    );
  };

  return (
    <Disclosure as="nav" className={`top-0 fixed bg-black w-full z-50`}>
      {({ open }) => (
        <>
          {/* Shared shell: logo (left) + desktop nav + mobile menu */}
          <div className="mx-auto max-w-6xl md:max-w-7xl px-4 py-2 sm:px-6 md:px-0 lg:px-8">
            <div className="relative flex h-16 items-center justify-between gap-3">
              {/* Logo — left on mobile & desktop, links home */}
              <Link
                to="/"
                className="relative z-10 flex shrink-0 items-center no-underline"
                aria-label="On The Strip Ticketing Home"
              >
                <CompanyLogo size={50} />
              </Link>

              {/* Desktop-only: horizontal nav */}
              <div className="hidden md:block">
                <div className="flex items-center justify-center space-x-4">
                  {getVisibleRoutes(routes).map((item) => (
                    <Fragment key={item.name || item.path || "nav-item"}>
                      {renderNavItem(item)}
                    </Fragment>
                  ))}
                </div>
              </div>

              {/* Mobile-only: hamburger / close */}
              <div className="flex items-center md:hidden">
                <Disclosure.Button className="relative rounded-md p-2 text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue-bright">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <IoClose size={22} color="white" />
                  ) : (
                    <IoMenu size={22} color="white" />
                  )}
                </Disclosure.Button>
              </div>
            </div>
          </div>

          {/* Mobile-only: slide-down panel (hidden from sm and up) — uses `renderMobileNavItem` */}
          <Disclosure.Panel className="md:hidden">
            <div className="space-y-1 px-2 pb-3 pt-2">
              {getVisibleRoutes(routes).map((item) => (
                <Fragment key={item.name || item.path || 'mobile-nav-item'}>
                  {renderMobileNavItem(item)}
                </Fragment>
              ))}
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}
